<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/vue-3';
import katex from 'katex';
import { Check, Pencil, Trash2, X } from 'lucide-vue-next';
import { computed, nextTick, ref, watch } from 'vue';
import 'katex/dist/katex.min.css';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

// ── Props ─────────────────────────────────────────────────────────────

const props = defineProps<NodeViewProps>();

const isBlock = computed(() => props.node.type.name === 'mathBlock');

// ── Editing state ─────────────────────────────────────────────────────

const isEditing = ref(false);
const draft = ref<string>(props.node.attrs.latex ?? '');
const textareaRef = ref<HTMLTextAreaElement | null>(null);

function startEdit() {
    draft.value = props.node.attrs.latex ?? '';
    isEditing.value = true;
    nextTick(() => textareaRef.value?.focus());
}

function cancelEdit() {
    isEditing.value = false;
}

function confirmEdit() {
    props.updateAttributes({ latex: draft.value });
    isEditing.value = false;
}

// Handle Ctrl+Enter to confirm, Escape to cancel
function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        confirmEdit();
    }

    if (e.key === 'Escape') {
        cancelEdit();
    }
}

// ── KaTeX render ──────────────────────────────────────────────────────

const rendered = computed<string>(() => {
    const latex = props.node.attrs.latex as string;

    if (!latex.trim()) {
        return '<span class="text-muted-foreground italic text-sm">Formule vide</span>';
    }

    try {
        return katex.renderToString(latex, {
            displayMode: isBlock.value,
            throwOnError: false,
            output: 'html',
        });
    } catch {
        return `<span class="text-destructive text-sm font-mono">${latex}</span>`;
    }
});

// Sync draft when node attrs change externally (e.g., undo/redo)
watch(
    () => props.node.attrs.latex as string,
    (v) => {
        if (!isEditing.value) {
            draft.value = v;
        }
    },
);
</script>

<template>
    <NodeViewWrapper
        :as="isBlock ? 'div' : 'span'"
        :class="[
            'math-node group relative',
            isBlock
                ? 'my-4 flex flex-col items-center rounded-md border border-border/40 bg-muted/20 px-4 py-3'
                : 'inline-flex items-center',
        ]"
    >
        <!-- ── Rendered formula ──────────────────────────────────────── -->
        <div
            v-if="!isEditing"
            class="math-rendered cursor-pointer"
            :class="isBlock ? 'text-center' : 'inline'"
            :title="props.node.attrs.latex"
            @dblclick="startEdit"
            v-html="rendered"
        />

        <!-- ── LaTeX editor ──────────────────────────────────────────── -->
        <div v-else class="w-full space-y-2">
            <Textarea
                ref="textareaRef"
                v-model="draft"
                class="font-mono text-sm"
                :rows="isBlock ? 4 : 2"
                placeholder="Saisir du LaTeX… ex: E = mc^2"
                @keydown="onKeydown"
            />
            <div class="flex items-center gap-1">
                <Button
                    size="sm"
                    variant="default"
                    class="h-7 px-3"
                    @click="confirmEdit"
                >
                    <Check class="mr-1 h-3.5 w-3.5" />
                    Valider
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    class="h-7 px-2"
                    @click="cancelEdit"
                >
                    <X class="h-3.5 w-3.5" />
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    class="ml-auto h-7 px-2 text-destructive hover:text-destructive"
                    @click="props.deleteNode()"
                >
                    <Trash2 class="h-3.5 w-3.5" />
                </Button>
            </div>
            <p v-if="isBlock" class="text-[11px] text-muted-foreground">
                Ctrl+Entrée pour valider · Échap pour annuler
            </p>
        </div>

        <!-- ── Quick-edit button (hover, rendered mode) ──────────────── -->
        <Button
            v-if="!isEditing"
            size="icon"
            variant="ghost"
            class="absolute top-1 right-1 h-5 w-5 opacity-0 transition-opacity group-hover:opacity-100"
            title="Modifier la formule"
            @click="startEdit"
        >
            <Pencil class="h-3 w-3" />
        </Button>
    </NodeViewWrapper>
</template>
